import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Card, ListGroup, Badge, OverlayTrigger, Tooltip, Modal, Button } from "react-bootstrap";
import { FiShoppingBag, FiCalendar, FiArrowUpRight, FiUsers, FiInfo, FiX } from "react-icons/fi";

const InfoOrdiniDettagliato = () => {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);

  useEffect(() => {
    fetch("ordini_urgenti.json")
      .then((res) => res.json())
      .then((json) => {
        setRawData(json?.ORDINI_URGENTI?.flat() || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Errore statistiche:", err);
        setLoading(false);
      });
  }, []);

  // Calcolo dei dati memorizzato per performance
  const stats = useMemo(() => {
    const adesso = new Date();
    
    // Otteniamo la data di oggi in formato YYYY-MM-DD usando l'ora LOCALE e non UTC
    const offset = adesso.getTimezoneOffset() * 60000;
    const oggiLocale = new Date(adesso - offset).toISOString().split('T')[0];

    const unaSettimanaFa = new Date();
    unaSettimanaFa.setDate(adesso.getDate() - 7);
    unaSettimanaFa.setHours(0, 0, 0, 0);

    const mapOggi = {};
    const mapSettimana = {};
    let totaleOggi = 0;
    let totaleSettimana = 0;

    rawData.forEach(item => {
      if (item["Tipo Riga"] === "T") {
        const importo = parseFloat(item["Importo"]) || 0;
        const fornitore = item["Ragione sociale"] || "Sconosciuto";
        
        const dataDocStr = item["Data Ordine"]?.split(" ")[0]; 
        const dataDoc = new Date(dataDocStr);

        if (dataDocStr === oggiLocale) {
          totaleOggi += importo;
          mapOggi[fornitore] = (mapOggi[fornitore] || 0) + importo;
        }

        if (dataDoc >= unaSettimanaFa && dataDoc <= adesso) {
          totaleSettimana += importo;
          mapSettimana[fornitore] = (mapSettimana[fornitore] || 0) + importo;
        }
      }
    });

    const formatLista = (obj) =>
      Object.entries(obj)
        .map(([nome, valore]) => ({ nome, valore }))
        .sort((a, b) => b.valore - a.valore);

    return {
      oggi: totaleOggi,
      listaOggi: formatLista(mapOggi),
      settimana: totaleSettimana,
      listaSettimana: formatLista(mapSettimana)
    };
  }, [rawData]);

  const formatEuro = (value) => 
    new Intl.NumberFormat('it-IT', { 
      style: 'currency', 
      currency: 'EUR', 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    }).format(value);

  const RenderLista = ({ lista, color, isFullscreen = false }) => (
    <div className="custom-scroll" style={{ height: isFullscreen ? "400px" : "160px", overflowY: "auto" }}>
      <ListGroup variant="flush" className="bg-transparent">
        {lista.length > 0 ? lista.map((f, i) => (
          <ListGroup.Item key={i} className="d-flex justify-content-between align-items-center px-0 py-2 border-bottom border-secondary border-opacity-10 bg-transparent">
            <div className="d-flex align-items-center overflow-hidden">
              <div className={`me-2 rounded-circle bg-${color}`} style={{ width: '5px', height: '5px', flexShrink: 0 }} />
              <small className="text-dark text-truncate" style={{ fontSize: isFullscreen ? '0.85rem' : '0.75rem', fontWeight: '500' }}>{f.nome}</small>
            </div>
            <span className="fw-bold text-dark ms-2" style={{ fontSize: isFullscreen ? '0.9rem' : '0.8rem' }}>{formatEuro(f.valore)}</span>
          </ListGroup.Item>
        )) : <div className="text-muted p-2 small fst-italic">Nessun ordine registrato</div>}
      </ListGroup>
    </div>
  );

  const renderTooltipSpiegazione = (props) => (
    <Tooltip id="info-acquisti-tooltip" {...props} style={{ ...props.style, fontSize: '0.85rem' }}>
      Questo widget fornisce un'<strong>Analisi degli Acquisti</strong> in tempo reale. Mostra il totale ordinato nella giornata di <strong>Oggi</strong> e negli <strong>Ultimi 7 Giorni</strong>, stilando una classifica dei top fornitori per volume di spesa.
    </Tooltip>
  );

  // Contenuto interno delle colonne (Oggi / Settimana), riutilizzabile tra Card e Modale
  const renderBodyContent = (isFullscreen = false) => (
    <Row className="g-0 flex-grow-1">
      {/* Sezione OGGI */}
      <Col xs={12} md={6} className={`p-4 ${isFullscreen ? 'p-md-5' : ''} border-end border-bottom border-md-bottom-0 border-secondary border-opacity-10 stat-card d-flex flex-column`}>
        <div className="d-flex align-items-center mb-4">
          <div className="icon-box text-danger p-2 rounded-3 me-3 d-flex align-items-center justify-content-center" style={{ width: isFullscreen ? "52px" : "42px", height: isFullscreen ? "52px" : "42px" }}>
            <FiShoppingBag size={isFullscreen ? 24 : 20} />
          </div>
          <div>
            <small className="text-uppercase text-muted fw-bold" style={{ fontSize: isFullscreen ? "0.7rem" : "0.6rem", letterSpacing: '0.5px' }}>Acquistato Oggi</small>
            <h2 className="mb-0 fw-bold text-dark" style={{ letterSpacing: '-1px', fontSize: isFullscreen ? "2.2rem" : "1.7rem" }}>
              {loading ? "..." : formatEuro(stats.oggi)}
            </h2>
          </div>
        </div>
        
        <div className="mb-2 d-flex align-items-center text-muted" style={{ fontSize: "0.65rem", fontWeight: '700', letterSpacing: '0.3px' }}>
          <FiUsers className="me-1 text-danger"/> TOP FORNITORI DEL GIORNO
        </div>
        <RenderLista lista={stats.listaOggi} color="danger" isFullscreen={isFullscreen} />
      </Col>

      {/* Sezione SETTIMANA */}
      <Col xs={12} md={6} className={`p-4 ${isFullscreen ? 'p-md-5' : ''} stat-card d-flex flex-column`}>
        <div className="d-flex align-items-center mb-4">
          <div className="icon-box text-warning p-2 rounded-3 me-3 d-flex align-items-center justify-content-center" style={{ width: isFullscreen ? "52px" : "42px", height: isFullscreen ? "52px" : "42px" }}>
            <FiCalendar size={isFullscreen ? 24 : 20} />
          </div>
          <div>
            <small className="text-uppercase text-muted fw-bold" style={{ fontSize: isFullscreen ? "0.7rem" : "0.6rem", letterSpacing: '0.5px' }}>Ultimi 7 Giorni</small>
            <h2 className="mb-0 fw-bold text-dark" style={{ letterSpacing: '-1px', fontSize: isFullscreen ? "2.2rem" : "1.7rem" }}>
              {loading ? "..." : formatEuro(stats.settimana)}
            </h2>
          </div>
        </div>

        <div className="mb-2 d-flex align-items-center text-muted" style={{ fontSize: "0.65rem", fontWeight: '700', letterSpacing: '0.3px' }}>
          <FiArrowUpRight className="me-1 text-warning"/> TREND SETTIMANALE
        </div>
        <RenderLista lista={stats.listaSettimana} color="warning" isFullscreen={isFullscreen} />
      </Col>
    </Row>
  );

  return (
    <Card className="border-0 shadow-lg" style={{ 
      borderRadius: "16px",
      overflow: "hidden" ,
      height: "350px",
      backgroundColor: "#ffffff"
    }}>
      <style>{`
        .custom-scroll::-webkit-scrollbar { width: 4px; }
        .custom-scroll::-webkit-scrollbar-track { background: transparent; }
        .custom-scroll::-webkit-scrollbar-thumb { background: #ced4da; border-radius: 10px; }
        .stat-card { transition: transform 0.2s ease-in-out; }
        .icon-box { background: rgba(0,0,0,0.03); border: 1px solid rgba(0,0,0,0.05); }
      `}</style>

      {/* Header Widget */}
      <div
        className="p-3 border-bottom d-flex justify-content-between align-items-center bg-white"
        style={{ zIndex: 110 }}
      >
        <div className="d-flex align-items-center" onClick={() => setShowFullscreenModal(true)} style={{ cursor: "pointer" }}>
          <span
            style={{
              fontWeight: "800",
              fontSize: "0.75rem",
              color: "#1a1a1a",
              letterSpacing: "1px",
              marginRight: "8px",
            }}
          >
            INFO ACQUISTI
          </span>
          
          <OverlayTrigger
            placement="bottom"
            delay={{ show: 200, hide: 200 }}
            overlay={renderTooltipSpiegazione}
          >
            <span onClick={(e) => e.stopPropagation()} style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
              <FiInfo size={14} color="#adb5bd" />
            </span>
          </OverlayTrigger>
        </div>
      </div>

      <Card.Body className="p-0 d-flex flex-column justify-content-center">
        {renderBodyContent(false)}
      </Card.Body>

      {/* MODALE TUTTO SCHERMO */}
      <Modal show={showFullscreenModal} onHide={() => setShowFullscreenModal(false)} fullscreen={true} animation={true}>
        <Modal.Body className="p-0 d-flex flex-column" style={{ background: "#f8f9fa" }}>
          <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center shadow-sm" style={{ zIndex: 110 }}>
            <div className="d-flex align-items-center">
              <span style={{ fontWeight: "800", fontSize: "0.9rem", color: "#1a1a1a", letterSpacing: "1px", marginRight: "8px" }}>
                INFO ACQUISTI 
              </span>
              <OverlayTrigger placement="bottom" overlay={renderTooltipSpiegazione}>
                <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}><FiInfo size={16} color="#adb5bd" /></span>
              </OverlayTrigger>
            </div>
            <Button variant="outline-dark" className="d-flex align-items-center gap-1 border-0" onClick={() => setShowFullscreenModal(false)} style={{ borderRadius: "10px", height: "38px" }}>
              <FiX size={16} /> Chiudi
            </Button>
          </div>
          
          <div className="flex-grow-1 p-4 d-flex align-items-center justify-content-center" style={{ overflowY: "auto" }}>
            <div className="bg-white shadow-sm rounded-4 border w-100 overflow-hidden" style={{ maxWidth: "1200px" }}>
              {renderBodyContent(true)}
            </div>
          </div>
        </Modal.Body>
      </Modal>

    </Card>
  );
};

export default InfoOrdiniDettagliato;